import { showNotification } from '@mantine/notifications';
import {
	apiQueryKey,
	useCancelAccessRequest,
	useDeleteAccessRequest,
	useRejectAccessRequest,
	type DataAccessRequestOut,
} from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
import { useMemo } from 'react';
import { queryClient } from '../../api';
import { ErrorLogs } from '../../components/ErrorLogs/ErrorLogs';
import {
	closeAllModals,
	openDeleteConfirmModal,
	openModal,
} from '../../components/ModalManager';
import { closeSpotlight } from '../../components/Spotlight';
import type { ICommandListItem } from '../../components/Spotlight/components/CommandPalette/constants';
import { BulkApproveModal } from './BulkApproveModal';

export function useActions(): ICommandListItem<DataAccessRequestOut>[] {
	const cancelAccessRequest = useCancelAccessRequest();

	const { mutate: rejectAccessRequest } = useRejectAccessRequest({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('integration_v2/data-access-requests'),
			});
		},
	});

	const { mutate: deleteAccessRequest } = useDeleteAccessRequest({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: apiQueryKey('integration_v2/data-access-requests'),
			});
		},
	});

	const actions = useMemo(
		() => [
			{
				id: 'actions::cancel',
				title: 'Revoke',
				name: 'Revoke',
				iconName: 'x' as const,
				hotkey: '/cn',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: (selected: DataAccessRequestOut[]) =>
					selected.every(
						(request) => request.status === 'approved' && request.can_manage
					),
				onClick: async (
					selected: DataAccessRequestOut[],
					clearSelection?: () => void
				) => {
					try {
						for (const req of selected) {
							// eslint-disable-next-line no-await-in-loop
							await cancelAccessRequest.mutateAsync({
								pathParams: { requestId: req.id },
								body: {},
							});
						}
						showNotification({
							title: 'Success',
							message: 'Successfully revoked access request(s)',
							color: 'green',
						});
						clearSelection?.();
						closeSpotlight('bulkActions');
					} catch (error) {
						openModal({
							title: 'Data access request error',
							size: 'sm',
							children: (
								<ErrorLogs
									title="Error revoking request"
									logs={[(error as { detail: string }).detail]}
								/>
							),
						});
						clearSelection?.();
					}
					queryClient.invalidateQueries({
						queryKey: apiQueryKey('integration_v2/data-access-requests'),
					});
				},
			},
			{
				id: 'actions::approve',
				title: 'Approve access',
				name: 'Approve access',
				iconName: null,
				hotkey: '/ap',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: (selected: DataAccessRequestOut[]) =>
					selected.every(
						(request) => request.status === 'pending' && request.can_manage
					),
				onClick: async (
					selected: DataAccessRequestOut[],
					clearSelection?: () => void
				) => {
					closeSpotlight('bulkActions');
					openModal({
						title: `Approve ${selected.length} access request${selected.length > 1 ? 's' : ''}`,
						size: 480,
						styles: {
							body: {
								maxHeight: '80vh',
								overflowY: 'auto',
							},
						},
						children: (
							<BulkApproveModal
								selected={selected}
								onClose={closeAllModals}
								clearSelection={clearSelection}
							/>
						),
						onClose: () => {
							clearSelection?.();
						},
					});
				},
			},
			{
				id: 'actions::reject',
				title: 'Deny access',
				name: 'Deny access',
				iconName: null,
				hotkey: '/rj',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: (selected: DataAccessRequestOut[]) =>
					selected.every(
						(request) => request.status === 'pending' && request.can_manage
					),
				onClick: async (
					selected: DataAccessRequestOut[],
					clearSelection?: () => void
				) => {
					try {
						await Promise.all(
							selected.map((request) =>
								rejectAccessRequest({
									pathParams: { requestId: request.id },
									body: {},
								})
							)
						);
						showNotification({
							title: 'Success',
							message: 'Successfully rejected access request(s)',
							color: 'green',
						});
						closeSpotlight('bulkActions');
						clearSelection?.();
					} catch (error) {
						openModal({
							title: 'Data access request error',
							size: 'sm',
							children: (
								<ErrorLogs
									title="Error rejecting request"
									logs={[(error as { detail: string }).detail]}
								/>
							),
						});
						clearSelection?.();
					}
				},
			},
			{
				id: 'actions::delete',
				title: 'Delete request',
				name: 'Delete request',
				iconName: null,
				hotkey: '/dl',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: (selected: DataAccessRequestOut[]) =>
					selected.every((request) => request.status === 'pending'),
				onClick: async (
					selected: DataAccessRequestOut[],
					clearSelection?: () => void
				) => {
					closeSpotlight('bulkActions');
					openDeleteConfirmModal({
						title: `Delete request${selected.length > 1 ? 's' : ''}?`,
						description: "You can't undo this action.",
						tone: 'critical',
						confirmLabel: `Delete request${selected.length > 1 ? 's' : ''}`,
						confirmProps: {
							variant: 'primary',
							tone: 'critical',
						},
						onConfirm: async () => {
							try {
								await Promise.all(
									selected.map((request) =>
										deleteAccessRequest({
											pathParams: { requestId: request.id },
										})
									)
								);
								showNotification({
									title: 'Success',
									message: 'Successfully deleted access request(s)',
									color: 'green',
								});
								clearSelection?.();
							} catch (error) {
								openModal({
									title: 'Data access request error',
									size: 'sm',
									children: (
										<ErrorLogs
											title="Error deleting request"
											logs={[(error as { detail: string }).detail]}
										/>
									),
								});
								clearSelection?.();
							}
						},
					});
				},
			},
			{
				id: 'actions::no_actions_available',
				title: 'No actions available',
				name: 'No actions available',
				iconName: 'infoCircle' as const,
				hotkey: '',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: (selected: DataAccessRequestOut[]) =>
					selected.length === 0 ||
					selected.every(
						(request) =>
							request.status === 'rejected' ||
							request.status === 'expired' ||
							request.status === 'cancelled'
					),
				disabled: true,
			},
		],
		[cancelAccessRequest, rejectAccessRequest, deleteAccessRequest]
	);

	return actions;
}
